'use client';

import { useEffect, useState } from 'react';
import { getPublishedChangelogEntries, ChangelogEntry } from '@/lib/changelog';
import { formatDistanceToNow } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Rocket, Bug, Zap, Megaphone } from 'lucide-react';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MarketingHeader, MarketingFooter } from '@/components/marketing';
import { ChangelogAdminBar } from '@/components/changelog-admin-bar';
import { ChangelogEntryAdminControls } from '@/components/changelog-entry-admin-controls';

const categoryConfig = {
  feature: {
    label: 'New Feature',
    icon: Rocket,
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
  },
  improvement: {
    label: 'Improvement',
    icon: Zap,
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
  },
  bugfix: {
    label: 'Bug Fix',
    icon: Bug,
    className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
  },
  announcement: {
    label: 'Announcement',
    icon: Megaphone,
    className: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
  }
};

export default function ChangelogPage() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadChangelog() {
      try {
        const data = await getPublishedChangelogEntries();
        setEntries(data);
      } catch (error) {
        console.error('Error loading changelog:', error);
      } finally {
        setLoading(false);
      }
    }

    loadChangelog();
  }, []);

  if (loading) {
    return (
      <>
        <MarketingHeader variant="light" />
        <ChangelogAdminBar />
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-32">
          <div className="max-w-4xl mx-auto px-4 py-16">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Changelog
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Loading updates...
              </p>
            </div>
          </div>
        </div>
        <MarketingFooter variant="light" />
      </>
    );
  }

  return (
    <>
      <MarketingHeader variant="light" />
      <ChangelogAdminBar />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-32">
        <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Changelog
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            New features, improvements, and bug fixes
          </p>
        </div>

        {/* Changelog Entries */}
        <div className="space-y-8">
          {entries.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">
                No changelog entries yet. Check back soon!
              </p>
            </Card>
          ) : (
            entries.map((entry) => {
              const config = categoryConfig[entry.category];
              const Icon = config.icon;
              const publishedDate = entry.publishedAt
                ? entry.publishedAt instanceof Timestamp
                  ? entry.publishedAt.toDate()
                  : new Date(entry.publishedAt)
                : new Date();

              return (
                <Card key={entry.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${config.className}`}>
                      <Icon className="w-5 h-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Link href={`/changelog/${entry.id}`}>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer">
                              {entry.title}
                            </h2>
                          </Link>
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <Badge variant="outline" className={config.className}>
                              {config.label}
                            </Badge>
                            <span>•</span>
                            <span>
                              {formatDistanceToNow(publishedDate, { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Image */}
                      {entry.imageUrl && (
                        <div className="my-4">
                          <img
                            src={entry.imageUrl}
                            alt={entry.title}
                            className="rounded-lg w-full object-cover max-h-96"
                          />
                        </div>
                      )}

                      {/* Content */}
                      <div className="prose prose-sm dark:prose-invert max-w-none mt-4
                        prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-white
                        prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3
                        prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-2
                        prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed
                        prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                        prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-li:my-1
                        prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {entry.content}
                        </ReactMarkdown>
                      </div>

                      {/* Tags */}
                      {entry.tags && entry.tags.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {entry.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* GitHub Link */}
                      {entry.githubCommitUrl && (
                        <div className="mt-4">
                          <a
                            href={entry.githubCommitUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            View commit on GitHub →
                          </a>
                        </div>
                      )}

                      {/* Admin Controls */}
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <ChangelogEntryAdminControls
                          entryId={entry.id!}
                          onDeleted={() => window.location.reload()}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>
      </div>
      <MarketingFooter variant="light" />
    </>
  );
}
